/**
 * Dictado por voz para reuniones (Web Speech API).
 * Idioma fijo es-ES: el CRM se usa en español aunque el front esté en EN.
 */

export function getSpeechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function isSpeechDictationSupported() {
  return Boolean(getSpeechRecognitionCtor());
}

/**
 * @param {object} opts
 * @param {(payload: { finalText: string, interimText: string, composed: string }) => void} opts.onUpdate
 * @param {(messageKey: string) => void} [opts.onError] — key i18n corta: unsupported | error
 * @param {() => void} [opts.onListeningChange]
 * @param {string} [opts.lang]
 */
export function startMeetingSpeechDictation({
  onUpdate,
  onError,
  onListeningChange,
  lang = "es-ES",
  getBaseText = () => "",
} = {}) {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) {
    onError?.("unsupported");
    return { stop: () => {} };
  }

  let stopped = false;
  let recognition = null;
  let finalText = "";
  const baseText = String(getBaseText() || "").trim();

  const emit = (interimText = "") => {
    const composed = [baseText, finalText, interimText]
      .map((s) => String(s || "").trim())
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    onUpdate?.({
      finalText: finalText.trim(),
      interimText: String(interimText || "").trim(),
      composed,
    });
  };

  const bind = (rec) => {
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = lang || "es-ES";
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const piece = event.results[i]?.[0]?.transcript || "";
        if (!piece) continue;
        if (event.results[i].isFinal) {
          finalText = [finalText, piece].filter(Boolean).join(" ");
        } else {
          interim += piece;
        }
      }
      emit(interim);
    };

    rec.onerror = (event) => {
      const code = String(event?.error || "");
      if (code === "aborted" || code === "no-speech") return;
      onError?.("error");
      stopped = true;
      onListeningChange?.(false);
    };

    rec.onend = () => {
      if (stopped) {
        onListeningChange?.(false);
        return;
      }
      // Chrome corta el continuous: reiniciar si seguimos escuchando.
      try {
        recognition = new Ctor();
        bind(recognition);
        recognition.start();
      } catch {
        stopped = true;
        onListeningChange?.(false);
      }
    };
  };

  try {
    recognition = new Ctor();
    bind(recognition);
    recognition.start();
    onListeningChange?.(true);
  } catch {
    onError?.("error");
    onListeningChange?.(false);
    return { stop: () => {} };
  }

  return {
    stop: () => {
      stopped = true;
      const rec = recognition;
      recognition = null;
      if (!rec) {
        onListeningChange?.(false);
        return;
      }
      try {
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        rec.stop();
      } catch {
        /* ignore */
      }
      onListeningChange?.(false);
    },
  };
}
